/**
 * Quote to Order — the worked example from the prototype and the first process to be
 * proven end to end (docs/BUILD.md §3). Used by tests, by `AI_MODE=replay`, and as seed data.
 *
 * Deliberately exercises: a margin decision with a rework loop, a wait step, a two-way
 * client decision with two distinct ends, a credit decision with a loop back through an
 * agent-performed step, hybrid work, and every human-default flag.
 */
const AE = 'Account Executive';
const SM = 'Sales Manager';
const FA = 'Finance Analyst';
const SOC = 'Sales Operations Coordinator';
const raci = (responsible, accountable, ...rest) => [
    { role: responsible, letter: 'R' },
    { role: accountable, letter: 'A' },
    ...rest,
];
const base = {
    description: null,
    systems: [],
    touchMinutes: null,
    waitMinutes: null,
    notes: null,
    requiresJudgment: false,
    involvesNegotiation: false,
    affectsClientTrust: false,
    automationSignals: null,
    whyNotAutomate: null,
    overrideReason: null,
};
export const quoteToOrder = {
    name: 'Quote to Order',
    purpose: 'Turn a qualified request into a confirmed sales order at or above the margin floor.',
    openQuestions: ['Who approves margin exceptions below floor when the Sales Manager is out?'],
    steps: [
        { ...base, key: 'request_received', name: 'Quote request received', role: AE, performedBy: 'human', stepType: 'start', raci: [], touchMinutes: 0 },
        {
            ...base,
            key: 'qualify_request',
            name: 'Qualify the request',
            description: 'Confirm the client, scope, timing, and budget are real before building a quote.',
            role: AE,
            performedBy: 'human',
            stepType: 'task',
            systems: [{ name: 'HubSpot', usage: 'reads' }],
            touchMinutes: 20,
            raci: raci(AE, SM),
            requiresJudgment: true,
            whyNotAutomate: 'Qualification is a conversation. Clients can tell when it is not.',
        },
        {
            ...base,
            key: 'build_quote',
            name: 'Build quote in HubSpot',
            role: AE,
            performedBy: 'hybrid',
            stepType: 'task',
            systems: [{ name: 'HubSpot', usage: 'writes' }, { name: 'NetSuite', usage: 'reads' }],
            touchMinutes: 45,
            raci: raci(AE, SM, { role: 'Solutions Engineer', letter: 'C' }),
            automationSignals: { ruleClarity: 4, dataAvailability: 4, exceptionRate: 'medium', volumePerMonth: 40 },
        },
        {
            ...base,
            key: 'check_margin',
            name: 'Margin above floor?',
            role: SM,
            performedBy: 'human',
            stepType: 'decision',
            touchMinutes: 5,
            raci: raci(SM, SM),
            requiresJudgment: true,
        },
        {
            ...base,
            key: 'revise_pricing',
            name: 'Revise pricing',
            role: AE,
            performedBy: 'human',
            stepType: 'task',
            systems: [{ name: 'HubSpot', usage: 'writes' }],
            touchMinutes: 30,
            raci: raci(AE, SM),
            involvesNegotiation: true,
            affectsClientTrust: true,
        },
        {
            ...base,
            key: 'send_quote',
            name: 'Send quote to client',
            role: AE,
            performedBy: 'human',
            stepType: 'task',
            systems: [{ name: 'HubSpot', usage: 'writes' }, { name: 'Outlook', usage: 'writes' }],
            touchMinutes: 10,
            raci: raci(AE, SM),
            affectsClientTrust: true,
        },
        {
            ...base,
            key: 'await_client',
            name: 'Await client decision',
            role: AE,
            performedBy: 'human',
            stepType: 'wait',
            touchMinutes: 0,
            waitMinutes: 2880,
            raci: raci(AE, SM),
        },
        { ...base, key: 'client_accepted', name: 'Client accepted?', role: AE, performedBy: 'human', stepType: 'decision', touchMinutes: 2, raci: raci(AE, SM) },
        {
            ...base,
            key: 'close_lost',
            name: 'Close quote as lost in HubSpot',
            role: AE,
            performedBy: 'agent',
            stepType: 'task',
            systems: [{ name: 'HubSpot', usage: 'writes' }],
            touchMinutes: 5,
            raci: raci(AE, SM),
            automationSignals: { ruleClarity: 5, dataAvailability: 5, exceptionRate: 'low', volumePerMonth: 15 },
        },
        { ...base, key: 'end_lost', name: 'Closed lost', role: AE, performedBy: 'human', stepType: 'end', raci: [], touchMinutes: 0 },
        {
            ...base,
            key: 'credit_check',
            name: 'Run credit check',
            role: FA,
            performedBy: 'agent',
            stepType: 'task',
            systems: [{ name: 'NetSuite', usage: 'reads' }],
            touchMinutes: 10,
            waitMinutes: 240,
            raci: raci(FA, 'Controller'),
            automationSignals: { ruleClarity: 5, dataAvailability: 5, exceptionRate: 'low', volumePerMonth: 25 },
        },
        { ...base, key: 'credit_ok', name: 'Credit approved?', role: FA, performedBy: 'hybrid', stepType: 'decision', touchMinutes: 5, raci: raci(FA, 'Controller') },
        {
            ...base,
            key: 'credit_hold',
            name: 'Resolve credit hold with client',
            role: AE,
            performedBy: 'human',
            stepType: 'task',
            touchMinutes: 40,
            waitMinutes: 1440,
            raci: raci(AE, SM, { role: FA, letter: 'C' }),
            involvesNegotiation: true,
            affectsClientTrust: true,
            whyNotAutomate: 'This is a hard conversation about money with a client we want to keep.',
        },
        {
            ...base,
            key: 'enter_order',
            name: 'Enter sales order in NetSuite',
            role: SOC,
            performedBy: 'agent',
            stepType: 'task',
            systems: [{ name: 'NetSuite', usage: 'writes' }, { name: 'HubSpot', usage: 'reads' }],
            touchMinutes: 15,
            raci: raci(SOC, SM, { role: FA, letter: 'I' }),
            automationSignals: { ruleClarity: 5, dataAvailability: 5, exceptionRate: 'low', volumePerMonth: 25 },
        },
        { ...base, key: 'end_order', name: 'Order confirmed', role: SOC, performedBy: 'human', stepType: 'end', raci: [], touchMinutes: 0 },
    ],
    transitions: [
        { from: 'request_received', to: 'qualify_request', condition: null, isDefault: false },
        { from: 'qualify_request', to: 'build_quote', condition: null, isDefault: false },
        { from: 'build_quote', to: 'check_margin', condition: null, isDefault: false },
        { from: 'check_margin', to: 'send_quote', condition: 'Yes', isDefault: true },
        { from: 'check_margin', to: 'revise_pricing', condition: 'No', isDefault: false },
        { from: 'revise_pricing', to: 'check_margin', condition: null, isDefault: false },
        { from: 'send_quote', to: 'await_client', condition: null, isDefault: false },
        { from: 'await_client', to: 'client_accepted', condition: null, isDefault: false },
        { from: 'client_accepted', to: 'credit_check', condition: 'Accepted', isDefault: true },
        { from: 'client_accepted', to: 'close_lost', condition: 'Declined', isDefault: false },
        { from: 'close_lost', to: 'end_lost', condition: null, isDefault: false },
        { from: 'credit_check', to: 'credit_ok', condition: null, isDefault: false },
        { from: 'credit_ok', to: 'enter_order', condition: 'Approved', isDefault: true },
        { from: 'credit_ok', to: 'credit_hold', condition: 'Hold', isDefault: false },
        { from: 'credit_hold', to: 'credit_check', condition: null, isDefault: false },
        { from: 'enter_order', to: 'end_order', condition: null, isDefault: false },
    ],
};
//# sourceMappingURL=quote-to-order.js.map